import React from 'react'
import PropTypes from 'prop-types'
import {
    Flex,
    Text,
    useDisclosure,
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerHeader,
    DrawerBody,
    Menu,
    MenuOptionGroup,
    MenuItemOption,
    Icon,
    IconButton,
    useStyleConfig
} from '@chakra-ui/react'
import {FormattedMessage, useIntl} from 'react-intl'
import { GlobeDark, CustomClose, CheckMarkIcon } from '../icons'

const CountrySelectorDrawer = ({
    countries,
    language,
    countryCode,
    handleLanguageChange,
    triggerRef,
    variant = 'header'
}) => {
    const intl = useIntl()
    const {isOpen, onClose, onToggle} = useDisclosure()
    const styles = useStyleConfig('CountrySelector', {variant})

    const getFooterLabel = (locale) => {
        if (!countries || !locale) {
            return
        }

        const [language, country] = locale.split('_')
        const localeTag = `${language}-${country}`

        const currency = countries[locale]?.currency
        const currencySymbol = currency
            ? new Intl.NumberFormat(localeTag, {
                  style: 'currency',
                  currency
              }).format(0).replace(/[\d.,]/g, '').trim()
            : ''

        return currencySymbol
            ? `${countryCode} - ${language.toUpperCase()} (${currencySymbol})`
            : `${countryCode} - ${language.toUpperCase()}`
    }

    // Expose onToggle and onClose to parent via ref
    React.useEffect(() => {
        if (triggerRef) {
            triggerRef.current = {onToggle, onClose, isOpen}
        }
    }, [triggerRef, onToggle, onClose, isOpen])

    return (
        <>
            <Flex {...styles.drawerTrigger}>
                {variant === 'footer' ? (
                    <>
                        <Icon as={GlobeDark} {...styles.globeIcon} />
                        <Text {...styles.footerLabel}>
                            {getFooterLabel(language)}
                        </Text>
                        <Text {...styles.footerChange}>
                            <FormattedMessage
                                defaultMessage="Change Country"
                                id="country_selector.change_country"
                            />
                        </Text>
                    </>
                ) : (
                    <>
                        <Text {...styles.drawerCode}>
                            {countryCode}
                        </Text>
                    </>
                )}
            </Flex>
            <Drawer isOpen={isOpen} onClose={onClose} placement="bottom">
                <DrawerOverlay />
                <DrawerContent {...styles.drawerContent}>
                    <IconButton
                        aria-label={intl.formatMessage({
                            id: 'header.button.close',
                            defaultMessage: 'Close'
                        })}
                        icon={<CustomClose />}
                        {...styles.drawerCloseButton}
                        onClick={onClose}
                    />
                    <DrawerBody {...styles.drawerBody}>
                        <Menu>
                            <MenuOptionGroup
                                defaultValue={language}
                                value={language}
                                onChange={(e) => handleLanguageChange(e)}
                            >
                                {countries &&
                                    Object.entries(countries).map(([lang, {label}], index) => {
                                        const menuItemStyles = styles.drawerMenuItem
                                        const isSelected = language === lang
                                        return (
                                            <MenuItemOption
                                                key={lang}
                                                value={lang}
                                                icon={null}
                                                iconSpacing={0}
                                                {...menuItemStyles}
                                            >
                                                <Flex {...styles.drawerMenuItemContent}>
                                                    <Text as="span" {...styles.drawerMenuItemLabel}>
                                                        {label}
                                                    </Text>
                                                    {isSelected ? (
                                                        <Icon as={CheckMarkIcon} {...styles.drawerMenuItemCheck} />
                                                    ) : null}
                                                </Flex>
                                            </MenuItemOption>
                                        )
                                    })}
                            </MenuOptionGroup>
                        </Menu>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </>
    )
}

CountrySelectorDrawer.propTypes = {
    countries: PropTypes.object,
    language: PropTypes.string,
    countryCode: PropTypes.string,
    handleLanguageChange: PropTypes.func,
    triggerRef: PropTypes.object
}

export default CountrySelectorDrawer
